<script setup lang="ts">
/**
 * "New intake agent", the one create dialog. It opens from the Intake Agents page
 * and owns the whole form — its own webhook fields, allowlist rows and CLM
 * selections — rather than borrowing the page's, which is what tied it to
 * `IntakeAgent.vue` before.
 *
 * It reports the finished agent through `created`; re-listing the page belongs
 * to the caller.
 */
import {
  type IntakeAgentCreate,
  type IntakeAgentInDb,
  type IntakeAgentUpdatePlaybooks,
  type IntakeAgentUpdatePromptCollections,
  type JiraIntegrationStatus,
  type SlackIntegrationStatus,
  IntegrationState,
  createIntakeAgent as createIntakeAgentApi,
  createAllowListEntry,
  updateIntakeAgentPlaybooks,
  updateIntakeAgentPromptCollections,
} from '@/api';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaTabBar from '@/components/design/composite/GaiaTabBar.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { fetchJiraIntegrationStatus } from '@/services/integration/jiraIntegrationService';
import {
  fetchSlackIntegrationStatus,
  hasAllSlackScopes,
  setAgentSlackEnabled,
} from '@/services/integration/slackIntegrationService';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { computed, ref, watch } from 'vue';
import {
  AGENT_MODAL_TABS,
  ALLOWLIST_ENTRY_TYPE_OPTIONS,
  CLM_SECTION_OPTIONS,
  SIGNING_STATUS_OPTIONS,
  WEBHOOK_METHOD_OPTIONS,
  capabilitiesError,
  createEquitySections,
  fetchPlaybooks,
  isValidDomain,
  isValidEmail,
  isValidUrl,
  onPlaybookSearchError,
  promptCollectionsError,
  useCapabilitiesOptions,
  useClmSections,
  useGaiaSectionOptions,
  usePromptCollectionOptions,
} from './intakeAgentForm';

const show = defineModel<boolean>('show', { required: true });

const emit = defineEmits<{
  /** The agent the backend returned, once its collections, playbooks, sections
      and allowlist entries have been written too. */
  created: [agent: IntakeAgentInDb];
}>();

const message = useGaiaToast();
const integrationsStore = useIntegrationsStore();
const promptCollectionsStore = usePromptCollectionsStore();

const capabilitiesOptions = useCapabilitiesOptions();
const promptCollectionOptions = usePromptCollectionOptions();
const gaiaSectionOptions = useGaiaSectionOptions();
const { isLoading: isLoadingClmSections, load: loadClmSections } = useClmSections();

type AgentForm = IntakeAgentCreate & {
  prompt_collections: string[];
  playbooks: string[];
  clm_sections: string;
  capabilities: string[];
  /** Not part of IntakeAgentCreate — applied with its own call once the agent exists. */
  slack_enabled: boolean;
};

function emptyForm(): AgentForm {
  return {
    name: '',
    description: '',
    allow_all_senders: true, // Default to true as requested
    allow_api_requests: false,
    respond_with_summary: false,
    prompt_collections: [],
    playbooks: [],
    clm_sections: 'none',
    capabilities: [],
    callback_webhook: null,
    filename_deny_list: null,
    inbound_document_signing_status: 'unknown',
    jira_webhook_enabled: false,
    slack_enabled: false,
  };
}

const form = ref<AgentForm>(emptyForm());
const activeTab = ref('capabilities');
const submitted = ref(false);
const saving = ref(false);

const filenameDenylist = ref<{ match_string: string; exact_match: boolean }[]>([]);
const allowlistEntries = ref<{ type: 'email' | 'domain' | 'user'; value: string }[]>([]);

// Webhook form data
const webhookUrl = ref('');
const webhookMethod = ref<'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'>('POST');
const webhookHeaders = ref<Record<string, string>>({});
const webhookTimeout = ref<string>('30');
const newHeaderKey = ref('');
const newHeaderValue = ref('');
const showWebhookForm = ref(false);

// CLM section selection
const selectedSpecificSections = ref<string[]>([]);
const selectedDefaultSection = ref<string | undefined>(undefined);

// The create modal has no agent yet, so there is no webhook config to read — only the org
// connection, to warn when enabling the webhook would not produce anything usable.
const jiraOrgStatus = ref<JiraIntegrationStatus | null>(null);
const jiraOrgStatusLoaded = ref(false);
const jiraWebhookLoading = ref(false);

const slackOrgStatus = ref<SlackIntegrationStatus | null>(null);
const slackOrgStatusLoaded = ref(false);
const slackOrgStatusLoading = ref(false);

const isSlackOrgConnected = computed(
  () => slackOrgStatus.value?.state === IntegrationState.CONNECTED
);

// Missing scopes only warn: the agent can still be created with the flag on, it just will not
// show up in Slack until the workspace is reconnected.
const slackNeedsReconnect = computed(
  () => isSlackOrgConnected.value && !hasAllSlackScopes(slackOrgStatus.value)
);

const isJiraOrgConnected = computed(
  () => jiraOrgStatus.value?.state === IntegrationState.CONNECTED
);

/* --------------------------------------------------------------- validation */

const nameError = computed<string | null>(() =>
  form.value.name.trim() ? null : 'Agent name is required'
);
const capabilitiesFieldError = computed(() => capabilitiesError(form.value.capabilities));
const promptCollectionsFieldError = computed(() =>
  promptCollectionsError(form.value.capabilities, form.value.prompt_collections)
);

// The webhook fields only exist while the form is open, so they only validate there.
const webhookUrlError = computed<string | null>(() => {
  if (!showWebhookForm.value) return null;
  if (!webhookUrl.value.trim()) return 'Webhook URL is required when webhook is enabled';
  if (!isValidUrl(webhookUrl.value)) return 'Please enter a valid webhook URL';
  return null;
});

const webhookTimeoutError = computed<string | null>(() => {
  if (!showWebhookForm.value) return null;
  if (webhookTimeout.value && isNaN(Number(webhookTimeout.value))) {
    return 'Webhook timeout must be a valid number';
  }
  return null;
});

/* -------------------------------------------------------------- open / reset */

// A fresh form on every open: the modal outlives each agent it creates, and a
// half-filled one from last time is never what the next agent should start from.
watch(show, async isOpen => {
  if (!isOpen) return;

  form.value = emptyForm();
  activeTab.value = 'capabilities';
  submitted.value = false;
  filenameDenylist.value = [];
  allowlistEntries.value = [];
  webhookUrl.value = '';
  webhookMethod.value = 'POST';
  webhookHeaders.value = {};
  webhookTimeout.value = '30';
  newHeaderKey.value = '';
  newHeaderValue.value = '';
  showWebhookForm.value = false;
  selectedSpecificSections.value = [];
  selectedDefaultSection.value = undefined;
  jiraOrgStatus.value = null;
  jiraOrgStatusLoaded.value = false;
  slackOrgStatus.value = null;
  slackOrgStatusLoaded.value = false;

  // The Review capability cannot be saved without a collection, and the
  // Integrations page — unlike the Intake Agents page — has never fetched them.
  if (promptCollectionsStore.collections.length === 0) {
    try {
      await promptCollectionsStore.fetchCollections();
    } catch (error) {
      logger.error('Failed to load prompt collections', error);
      message.error('Failed to load prompt collections');
    }
  }

  if (integrationsStore.activeIntegrations.gaia) {
    try {
      await loadClmSections();
    } catch (error) {
      logger.error('Failed to load CLM sections:', error);
      message.error('Failed to load CLM sections');
    }
  }
});

async function handleTabChange(tab: string): Promise<void> {
  activeTab.value = tab;

  if (tab === 'slack' && !slackOrgStatusLoaded.value) {
    slackOrgStatusLoading.value = true;
    try {
      slackOrgStatus.value = await fetchSlackIntegrationStatus();
      slackOrgStatusLoaded.value = true;
    } catch (error) {
      logger.error('Failed to load Slack status:', error);
      message.error(extractErrorDetail(error, 'Failed to load Slack status.'));
    } finally {
      slackOrgStatusLoading.value = false;
    }
    return;
  }

  if (tab !== 'jira' || jiraOrgStatusLoaded.value) return;

  jiraWebhookLoading.value = true;
  try {
    jiraOrgStatus.value = await fetchJiraIntegrationStatus();
    jiraOrgStatusLoaded.value = true;
  } catch (error) {
    logger.error('Failed to load the Jira integration status:', error);
    message.error(extractErrorDetail(error, 'Failed to load the Jira integration status.'));
  } finally {
    jiraWebhookLoading.value = false;
  }
}

function handleCapabilitiesChange(value: string[]): void {
  form.value.capabilities = value;
  // Clear prompt collections if review capability is removed
  if (!value.includes('review')) {
    form.value.prompt_collections = [];
  }
  // Clear playbooks if playbook capability is removed
  if (!value.includes('playbook')) {
    form.value.playbooks = [];
  }
}

/* ------------------------------------------------------------ row management */

function addHeader(): void {
  const key = newHeaderKey.value.trim();
  const value = newHeaderValue.value.trim();
  if (key && value) {
    webhookHeaders.value[key] = value;
    newHeaderKey.value = '';
    newHeaderValue.value = '';
  }
}

function removeHeader(key: string): void {
  delete webhookHeaders.value[key];
}

function headerValueUpdater(key: string): (val: string) => void {
  return val => {
    webhookHeaders.value = { ...webhookHeaders.value, [key]: val };
  };
}

function clearWebhookSettings(): void {
  showWebhookForm.value = false;
  form.value.callback_webhook = null;
  webhookUrl.value = '';
  webhookMethod.value = 'POST';
  webhookHeaders.value = {};
  webhookTimeout.value = '30';
}

function addFilenameDenylistItem(): void {
  filenameDenylist.value.push({ match_string: '', exact_match: false });
}

function removeFilenameDenylistItem(index: number): void {
  filenameDenylist.value.splice(index, 1);
}

function addAllowlistEntry(): void {
  allowlistEntries.value.push({ type: 'email', value: '' });
}

function removeAllowlistEntry(index: number): void {
  allowlistEntries.value.splice(index, 1);
}

/* ------------------------------------------------------------------- submit */

/** Best-effort: a rejected entry warns and the agent still stands. */
async function createAllowlistEntries(agentUid: string): Promise<void> {
  for (const entry of allowlistEntries.value) {
    if (!entry.value.trim()) continue;

    if (entry.type === 'domain' && !isValidDomain(entry.value)) {
      message.warning(`Invalid domain: ${entry.value}`);
      continue;
    }
    if (entry.type !== 'domain' && !isValidEmail(entry.value)) {
      message.warning(`Invalid email: ${entry.value}`);
      continue;
    }

    try {
      const entryResponse = await createAllowListEntry({
        path: { intake_agent_uid: agentUid },
        body: {
          email: entry.type === 'domain' ? '' : entry.value,
          domain: entry.type === 'domain' ? entry.value : '',
          is_user: entry.type === 'user',
          is_domain: entry.type === 'domain',
        },
      });
      if (entryResponse.error) {
        throw new Error(
          extractErrorDetail(entryResponse.error, 'Failed to create allowlist entry')
        );
      }
    } catch (error) {
      logger.error('Failed to create allowlist entry:', error);
      message.warning(`Failed to add allowlist entry: ${entry.value}`);
    }
  }
}

async function handleCreate(): Promise<void> {
  // Set form as submitted so the fields show their errors inline
  submitted.value = true;

  // A field on a tab the user never opened still has to be reported, so the
  // first failure also names the tab to switch to.
  const capabilityErrors = [
    nameError.value,
    capabilitiesFieldError.value,
    promptCollectionsFieldError.value,
  ].filter((error): error is string => Boolean(error));
  const advancedErrors = [webhookUrlError.value, webhookTimeoutError.value].filter(
    (error): error is string => Boolean(error)
  );

  if (capabilityErrors.length > 0 || advancedErrors.length > 0) {
    message.error(capabilityErrors[0] ?? advancedErrors[0]);
    activeTab.value = capabilityErrors.length > 0 ? 'capabilities' : 'advanced';
    return;
  }

  saving.value = true;
  try {
    const webhookUrlTrimmed = webhookUrl.value.trim();
    const descriptionTrimmed = form.value.description?.trim() ?? '';
    const callbackWebhook =
      showWebhookForm.value && webhookUrlTrimmed.length > 0
        ? {
            method: webhookMethod.value,
            url: webhookUrlTrimmed,
            headers: Object.keys(webhookHeaders.value).length > 0 ? webhookHeaders.value : null,
            timeout_s: webhookTimeout.value ? Number(webhookTimeout.value) : undefined,
          }
        : null;

    const createResponse = await createIntakeAgentApi({
      body: {
        name: form.value.name,
        description: descriptionTrimmed.length > 0 ? descriptionTrimmed : null,
        allow_all_senders: form.value.allow_all_senders,
        allow_api_requests: form.value.allow_api_requests,
        respond_with_summary: form.value.capabilities.includes('respond_with_summary'),
        run_playbook: form.value.capabilities.includes('playbook'),
        callback_webhook: callbackWebhook,
        filename_deny_list: filenameDenylist.value.length > 0 ? filenameDenylist.value : null,
        inbound_document_signing_status: form.value.inbound_document_signing_status,
        jira_webhook_enabled: form.value.jira_webhook_enabled,
      },
    });
    if (createResponse.error) {
      throw new Error(extractErrorDetail(createResponse.error, 'Failed to create intake agent'));
    }
    const agent = createResponse.data;

    // If we have selected prompt collections and review capability is enabled, update the agent with them
    if (form.value.prompt_collections.length > 0 && form.value.capabilities.includes('review')) {
      const promptCollectionsUpdate: IntakeAgentUpdatePromptCollections = {
        prompt_collection_uids: form.value.prompt_collections,
      };

      const updateResponse = await updateIntakeAgentPromptCollections({
        path: { intake_agent_uid: agent.uid },
        body: promptCollectionsUpdate,
      });
      if (updateResponse.error) {
        throw new Error(
          extractErrorDetail(
            updateResponse.error,
            "Failed to update the agent's prompt collections"
          )
        );
      }
    }

    // If we have selected playbooks and playbook capability is enabled, link them to the agent
    if (form.value.playbooks.length > 0 && form.value.capabilities.includes('playbook')) {
      const playbooksUpdate: IntakeAgentUpdatePlaybooks = {
        playbook_uids: form.value.playbooks,
      };

      const playbooksResponse = await updateIntakeAgentPlaybooks({
        path: { intake_agent_uid: agent.uid },
        body: playbooksUpdate,
      });
      if (playbooksResponse.error) {
        throw new Error(
          extractErrorDetail(playbooksResponse.error, "Failed to update the agent's playbooks")
        );
      }
    }

    // Create equity sections efficiently if storage capability is enabled
    if (form.value.capabilities.includes('import_em')) {
      await createEquitySections(
        agent.uid,
        form.value.clm_sections,
        selectedSpecificSections.value,
        selectedDefaultSection.value
      );
    }

    // `slack_enabled` is not part of IntakeAgentCreate, so the agent has to exist first.
    if (form.value.slack_enabled) {
      await setAgentSlackEnabled(agent.uid, true);
    }

    // Read before the form is reset by the close below.
    const shouldWriteAllowlist = !form.value.allow_all_senders;

    message.success('Intake agent created successfully');
    show.value = false;

    if (shouldWriteAllowlist) {
      await createAllowlistEntries(agent.uid);
    }

    emit('created', agent);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to create intake agent');
    logger.error('Failed to create intake agent', error);
  } finally {
    saving.value = false;
  }
}

function openApiDocs(): void {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- falsy values must fall through here
  const apiBaseUrl = import.meta.env.VITE_API_URL || window.location.origin;
  window.open(`${apiBaseUrl}/public-api/redoc`, '_blank');
}
</script>

<template>
  <gaia-modal v-model:show="show" width="800px">
    <template #header>
      <n-flex align="center" :size="12" :wrap="false">
        <gaia-text size="dense" weight="semibold" tone="ink" :style="{ whiteSpace: 'nowrap' }">
          Agent Name:
        </gaia-text>
        <gaia-input
          v-model:value="form.name"
          placeholder="Enter agent name"
          :style="{ flex: 1, maxWidth: '300px' }"
          :status="submitted && nameError ? 'error' : undefined"
        />
      </n-flex>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <gaia-tab-bar :items="AGENT_MODAL_TABS" :active="activeTab" @select="handleTabChange" />

      <!-- Capabilities Tab -->
      <n-flex v-show="activeTab === 'capabilities'" vertical :size="16" :wrap="false">
        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            v-model:value="form.description"
            :rows="2"
            :autosize="false"
            placeholder="What this agent is for"
          />
        </gaia-form-field>

        <gaia-form-field
          label="Capabilities"
          required
          hint="Select the capabilities this intake agent will provide."
          :error="(submitted && capabilitiesFieldError) || undefined"
        >
          <gaia-select
            :value="form.capabilities"
            multiple
            filterable
            placeholder="Select capabilities"
            :options="capabilitiesOptions"
            :status="submitted && capabilitiesFieldError ? 'error' : undefined"
            :style="{ width: '100%' }"
            @update:value="handleCapabilitiesChange"
          />
        </gaia-form-field>

        <!-- Review Capability Section -->
        <n-flex v-if="form.capabilities.includes('review')" vertical :size="10" :wrap="false">
          <gaia-section-header variant="rule">Review Capability Settings</gaia-section-header>
          <gaia-form-field
            label="Prompt Collections"
            required
            hint="The prompt collections that can run on files submitted to this agent. The system will automatically choose the best matching collection."
            :error="(submitted && promptCollectionsFieldError) || undefined"
          >
            <gaia-select
              v-model:value="form.prompt_collections"
              multiple
              filterable
              placeholder="Select prompt collections"
              :options="promptCollectionOptions"
              :status="submitted && promptCollectionsFieldError ? 'error' : undefined"
              :style="{ width: '100%' }"
            />
          </gaia-form-field>
        </n-flex>

        <!-- Playbook Capability Section -->
        <n-flex v-if="form.capabilities.includes('playbook')" vertical :size="10" :wrap="false">
          <gaia-section-header variant="rule">Playbook Capability Settings</gaia-section-header>
          <gaia-form-field
            label="Playbooks"
            hint="The playbooks this agent can run on submitted files."
          >
            <gaia-remote-select
              v-model:value="form.playbooks"
              multiple
              clearable
              placeholder="Search playbooks"
              :debounce="400"
              :fetch="fetchPlaybooks"
              :style="{ width: '100%' }"
              @error="onPlaybookSearchError"
            />
          </gaia-form-field>
        </n-flex>

        <!-- Storage Capability Section -->
        <n-flex
          v-if="
            form.capabilities.includes('import_em') && integrationsStore.activeIntegrations.gaia
          "
          vertical
          :size="10"
          :wrap="false"
        >
          <gaia-section-header variant="rule"
            >Storage Capability Settings (CLM)</gaia-section-header
          >

          <gaia-form-field label="Are the documents signed?">
            <gaia-select
              v-model:value="form.inbound_document_signing_status"
              :options="SIGNING_STATUS_OPTIONS"
              :style="{ width: '100%' }"
            />
          </gaia-form-field>

          <gaia-form-field label="Where to store">
            <gaia-select
              v-model:value="form.clm_sections"
              filterable
              placeholder="Select CLM section option"
              :options="CLM_SECTION_OPTIONS"
              :disabled="isLoadingClmSections"
              :loading="isLoadingClmSections"
              :style="{ width: '100%' }"
            />
          </gaia-form-field>

          <!-- Conditional dropdown for specific sections -->
          <gaia-form-field
            v-if="form.clm_sections === 'specific'"
            label="Sorting Sections"
            hint="The possible CLM sections where the file should be stored. The system will automatically choose the best matching section."
          >
            <gaia-select
              v-model:value="selectedSpecificSections"
              :options="gaiaSectionOptions"
              placeholder="Select specific sections..."
              multiple
              filterable
              :loading="isLoadingClmSections"
              :disabled="isLoadingClmSections"
              :style="{ width: '100%' }"
            />
          </gaia-form-field>

          <!-- Default section selection -->
          <gaia-form-field
            v-if="form.clm_sections === 'specific' || form.clm_sections === 'all'"
            label="Default Section"
            hint="This section will be chosen if the document can't be processed or there is a technical issue."
          >
            <gaia-select
              v-model:value="selectedDefaultSection"
              :options="gaiaSectionOptions"
              placeholder="Select default section (optional)..."
              filterable
              clearable
              :loading="isLoadingClmSections"
              :disabled="isLoadingClmSections"
              :style="{ width: '100%' }"
            />
          </gaia-form-field>
        </n-flex>
      </n-flex>

      <!-- Permissions Tab -->
      <n-flex v-show="activeTab === 'permissions'" vertical :size="12" :wrap="false">
        <gaia-toggle-row
          card
          label="Allow All Senders"
          :hint="form.allow_all_senders ? 'All senders allowed' : 'Only allowlist entries allowed'"
          :checked="form.allow_all_senders"
          @update:checked="form.allow_all_senders = $event"
        />

        <gaia-toggle-row
          card
          label="API Access"
          :hint="form.allow_api_requests ? 'API requests allowed' : 'API requests blocked'"
          :checked="form.allow_api_requests"
          @update:checked="form.allow_api_requests = $event"
        />

        <n-flex v-if="form.allow_api_requests" vertical :size="6" :wrap="false">
          <n-flex :size="8">
            <gaia-button size="small" disabled>Copy API Token</gaia-button>
            <gaia-button size="small" @click="openApiDocs">View API Docs</gaia-button>
          </n-flex>
          <gaia-text size="micro" tone="muted">
            Note: API token will be generated when the agent is created
          </gaia-text>
        </n-flex>

        <!-- Allowlist Management (only show if allow_all_senders is false) -->
        <n-flex v-if="!form.allow_all_senders" vertical :size="10" :wrap="false">
          <gaia-section-header variant="rule">Allowlist Entries</gaia-section-header>
          <gaia-text size="control" tone="subtle">
            Only senders in the allowlist will be able to send emails to this agent.
          </gaia-text>

          <n-flex
            v-for="(entry, index) in allowlistEntries"
            :key="index"
            align="center"
            :size="8"
            :wrap="false"
          >
            <gaia-select
              v-model:value="entry.type"
              :options="ALLOWLIST_ENTRY_TYPE_OPTIONS"
              :style="{ width: '110px', flex: 'none' }"
            />
            <gaia-input
              v-model:value="entry.value"
              :placeholder="entry.type === 'domain' ? 'example.com' : 'user@example.com'"
              :style="{ flex: 1 }"
            />
            <gaia-delete-button intent="remove" @click="removeAllowlistEntry(index)" />
          </n-flex>

          <gaia-add-button subject="Allowlist Entry" variant="dashed" @click="addAllowlistEntry" />
        </n-flex>
      </n-flex>

      <!-- Advanced Settings Tab -->
      <n-flex v-show="activeTab === 'advanced'" vertical :size="16" :wrap="false">
        <!-- Webhook Settings Section -->
        <n-flex vertical :size="10" :wrap="false">
          <gaia-section-header variant="rule">Webhook Notifications</gaia-section-header>
          <gaia-text size="control" tone="subtle">
            Get notified when documents are processed by this agent.
          </gaia-text>

          <template v-if="showWebhookForm">
            <gaia-form-field
              label="Webhook URL"
              required
              hint="Receives a notification when documents are processed by this agent."
              :error="(submitted && webhookUrlError) || undefined"
            >
              <gaia-input
                v-model:value="webhookUrl"
                placeholder="Enter callback webhook URL"
                clearable
                :status="submitted && webhookUrlError ? 'error' : undefined"
                :style="{ width: '100%' }"
              />
            </gaia-form-field>

            <n-flex :size="12" align="flex-start">
              <gaia-form-field label="HTTP Method" :style="{ width: '200px' }">
                <gaia-select
                  v-model:value="webhookMethod"
                  :options="WEBHOOK_METHOD_OPTIONS"
                  placeholder="Select HTTP method"
                  :style="{ width: '100%' }"
                />
              </gaia-form-field>
              <gaia-form-field
                label="Timeout (seconds)"
                :style="{ width: '200px' }"
                :error="(submitted && webhookTimeoutError) || undefined"
              >
                <gaia-input
                  v-model:value="webhookTimeout"
                  placeholder="30"
                  :status="submitted && webhookTimeoutError ? 'error' : undefined"
                  :style="{ width: '100%' }"
                />
              </gaia-form-field>
            </n-flex>

            <gaia-form-field label="Headers" hint="Optional">
              <n-flex vertical :size="8" :wrap="false" :style="{ width: '100%' }">
                <n-flex
                  v-for="(value, key, idx) in webhookHeaders"
                  :key="key + idx"
                  align="center"
                  :size="8"
                  :wrap="false"
                >
                  <gaia-input
                    :value="key"
                    disabled
                    placeholder="Header Key"
                    :style="{ width: '160px' }"
                  />
                  <gaia-input
                    :value="value"
                    placeholder="Header Value"
                    :style="{ flex: 1 }"
                    @update:value="headerValueUpdater(key)"
                  />
                  <gaia-delete-button intent="remove" @click="removeHeader(key)" />
                </n-flex>

                <n-flex align="center" :size="8" :wrap="false">
                  <gaia-input
                    v-model:value="newHeaderKey"
                    placeholder="Header Key"
                    :style="{ width: '160px' }"
                  />
                  <gaia-input
                    v-model:value="newHeaderValue"
                    placeholder="Header Value"
                    :style="{ flex: 1 }"
                  />
                  <gaia-button size="small" @click="addHeader">Add Header</gaia-button>
                </n-flex>
              </n-flex>
            </gaia-form-field>

            <n-flex justify="end" :size="8">
              <gaia-button size="small" type="error" tertiary @click="clearWebhookSettings">
                Remove Webhook
              </gaia-button>
            </n-flex>
          </template>

          <gaia-add-button
            v-else
            subject="Webhook Settings"
            variant="dashed"
            @click="showWebhookForm = true"
          />
        </n-flex>

        <!-- Filename Exclude List Section -->
        <n-flex vertical :size="10" :wrap="false">
          <gaia-section-header variant="rule">Filename Exclude List</gaia-section-header>
          <gaia-text size="control" tone="subtle">
            Block files with specific names or extensions from being processed by this agent.
          </gaia-text>

          <n-flex
            v-for="(item, index) in filenameDenylist"
            :key="index"
            align="center"
            :size="8"
            :wrap="false"
          >
            <gaia-input
              v-model:value="item.match_string"
              placeholder="e.g., .pdf or filename.txt"
              :style="{ flex: 1 }"
            />
            <gaia-switch v-model:value="item.exact_match" :style="{ flex: 'none' }" />
            <gaia-text size="micro" tone="muted" :style="{ width: '96px', flex: 'none' }">
              {{ item.exact_match ? 'Exact match' : 'Contains' }}
            </gaia-text>
            <gaia-delete-button intent="remove" @click="removeFilenameDenylistItem(index)" />
          </n-flex>

          <gaia-add-button
            subject="Exclude List Entry"
            variant="dashed"
            @click="addFilenameDenylistItem"
          />
        </n-flex>
      </n-flex>

      <!-- Jira Tab -->
      <n-flex v-show="activeTab === 'jira'" vertical :size="12" :wrap="false">
        <gaia-spinner :show="jiraWebhookLoading">
          <n-flex vertical :size="12" :wrap="false">
            <gaia-section-header variant="rule">Jira WebHook</gaia-section-header>
            <gaia-text size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
              Let this agent pick up files attached to Jira issues. The WebHook URL is minted with
              the agent — open its Jira tab afterwards to copy the URL and register it in Jira.
            </gaia-text>

            <gaia-banner v-if="jiraOrgStatusLoaded && !isJiraOrgConnected" type="warning" plain>
              Jira is not connected for this organisation, so incoming events are acknowledged and
              then dropped. Connect Jira under
              <router-link to="/settings/service-account-token">Integrations</router-link>.
            </gaia-banner>

            <gaia-toggle-row
              card
              label="Enable the Jira WebHook for this agent"
              hint="A WebHook URL is minted with the agent."
              :checked="Boolean(form.jira_webhook_enabled)"
              @update:checked="form.jira_webhook_enabled = $event"
            />
          </n-flex>
        </gaia-spinner>
      </n-flex>

      <!-- Slack Tab -->
      <n-flex v-show="activeTab === 'slack'" vertical :size="12" :wrap="false">
        <gaia-spinner :show="slackOrgStatusLoading">
          <n-flex vertical :size="12" :wrap="false">
            <gaia-section-header variant="rule">Slack</gaia-section-header>
            <gaia-text size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
              Let workspace users pick this agent in Slack's /gaia-submit. It is turned on right
              after the agent is created, and can be changed any time from its Slack tab.
            </gaia-text>

            <gaia-banner v-if="slackOrgStatusLoaded && !isSlackOrgConnected" type="warning" plain>
              Slack is not connected for this organisation, so this agent will not appear in
              /gaia-submit. Connect Slack under
              <router-link to="/settings/service-account-token">Integrations</router-link>.
            </gaia-banner>

            <gaia-banner v-else-if="slackNeedsReconnect" type="warning" plain>
              This Slack workspace is missing permissions Gaia now needs, so the agent will not show
              up in /gaia-submit even once enabled. Reconnect Slack under
              <router-link to="/settings/service-account-token">Integrations</router-link>.
            </gaia-banner>

            <gaia-toggle-row
              card
              label="Available in Slack"
              hint="Shown in the /gaia-submit agent picker."
              :checked="form.slack_enabled"
              @update:checked="form.slack_enabled = $event"
            />
          </n-flex>
        </gaia-spinner>
      </n-flex>

      <!-- Warning note below tabs - always visible -->
      <gaia-banner v-if="form.allow_all_senders" type="info" title="Note">
        Everyone can send emails to this agent. You can configure specific allowlist entries in the
        Permissions tab after creation if needed.
      </gaia-banner>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button size="small" @click="show = false">Cancel</gaia-button>
        <gaia-button type="primary" size="small" :loading="saving" @click="handleCreate">
          Create
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
